// The Supabase-backed Store, isolated from the rest of the app.
//
// This does NOT reimplement app.js's storage logic. Instead it implements
// the exact `window.claude` capability surface app.js's connect()/flush()/
// startOnline() already speak (a small Firestore-like `db` with
// `collection(path).onSnapshot(cb, errCb)` and `doc(path).get/set/update/
// delete()`, plus `assets.upload/delete/url` and `downloads.save`). That
// lets 100% of the existing card/search/wheel/meeting logic run completely
// unmodified against Supabase — see docs/dev/architecture.md's "Boot and
// storage" section for what that logic already does with these calls.
//
// Path <-> table mapping (see docs/dev/data-model.md "Stage 2" schema and
// supabase/migrations/0001_init.sql + 0002_server_store.sql + 0003_page_column.sql):
//   collection("cards")  <-> cards table (+ checklist_items, history, card_links)
//   collection("sess")   <-> meeting_notes table, doc id "<cardId>~<sessId>"
//   collection("pages")  <-> cards.page (its own column, not part of fields — see
//                            pageDocRef() for why: it needs to be written without
//                            racing the fields-column read-modify-write below)
//   doc("meta/folders")  <-> folders table
//   doc("meta/types")    <-> card_types table + workspaces.settings
//
// `fields` (cards.fields jsonb) holds everything else the schema has no column
// for: f (custom fields), src, notes, example, archived, template, remind, rpt (repeating
// tasks), files[], and the synthetic key _links (web links) that the task brief calls out
// as not having a dedicated column.

const BUCKET = "tabwheel-files";
const REFRESH_MS = 25000;

function groupBy(rows, key){
  var m = new Map();
  (rows || []).forEach(function(r){
    if(!m.has(r[key])) m.set(r[key], []);
    m.get(r[key]).push(r);
  });
  return m;
}

function toDbError(err){
  var msg = (err && err.message) || "";
  var code = err && err.code;
  if(code === "42501" || /permission denied|row-level security/i.test(msg)) return {code:"invalid_argument", cause:err};
  if((err && err.status === 401) || /jwt/i.test(msg)) return {code:"revoked", cause:err};
  return {code:"unknown", cause:err};
}

function randomId(){
  if(window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
}

function safeName(name){
  return String(name || "file").replace(/[^A-Za-z0-9._-]/g, "_").slice(-140);
}

/**
 * @param {object} opts
 * @param {import('@supabase/supabase-js').SupabaseClient} opts.supabase
 * @param {string} opts.workspaceId
 * @param {string} opts.userId
 */
export function createClaudeShim(opts){
  var supabase = opts.supabase, workspaceId = opts.workspaceId, userId = opts.userId;

  // ---------------- read side: one fetch feeds all 5 "listeners" ----------------
  var listeners = {cards:[], sess:[], pages:[], types:[], folders:[]};
  var lastGood = null;
  var refreshing = null;
  var timerStarted = false;

  function cardRowToDoc(row, check, hist, rel){
    var f = row.fields || {};
    return {
      id: row.id, type: row.type_key, title: row.title || "", subtitle: row.subtitle || "",
      status: row.status || "",
      f: f.f || {},
      check: check.map(function(x){ return {id:x.id, t:x.text, done:!!x.done}; }),
      hist: hist.map(function(x){ return {at:x.created_at, w:x.text}; }),
      src: f.src || "",
      tags: row.tags || [],
      links: f._links || [],
      files: f.files || [],
      notes: f.notes || "",
      fav: Array.isArray(row.fav_by) && userId ? row.fav_by.indexOf(userId) >= 0 : false,
      folder: row.folder_id || "",
      rel: rel,
      example: !!f.example,
      updated: row.updated_at || "",
      deleted: row.deleted_at || "",
      archived: f.archived || "",
      template: !!f.template,
      remind: f.remind || "",
      rpt: f.rpt || null,
      home: !!row.home
    };
  }

  function loadAll(){
    return Promise.all([
      supabase.from("cards").select("*"),
      supabase.from("checklist_items").select("*").order("pos", {ascending:true}),
      supabase.from("history").select("*").order("created_at", {ascending:true}),
      supabase.from("meeting_notes").select("*").order("date", {ascending:true}),
      supabase.from("card_links").select("*"),
      supabase.from("folders").select("*"),
      supabase.from("card_types").select("*"),
      supabase.from("workspaces").select("settings").eq("id", workspaceId).maybeSingle()
    ]).then(function(results){
      var i; for(i = 0; i < results.length; i++){ if(results[i].error) throw toDbError(results[i].error); }
      var cardsQ = results[0].data || [], checkQ = results[1].data || [], histQ = results[2].data || [],
          sessQ = results[3].data || [], linksQ = results[4].data || [], foldersQ = results[5].data || [],
          typesQ = results[6].data || [], wsSettings = (results[7].data && results[7].data.settings) || {};

      var checkByCard = groupBy(checkQ, "card_id");
      var histByCard = groupBy(histQ, "card_id");
      var relByCard = new Map();
      linksQ.forEach(function(l){
        if(!relByCard.has(l.from_id)) relByCard.set(l.from_id, []);
        if(!relByCard.has(l.to_id)) relByCard.set(l.to_id, []);
        if(relByCard.get(l.from_id).indexOf(l.to_id) < 0) relByCard.get(l.from_id).push(l.to_id);
        if(relByCard.get(l.to_id).indexOf(l.from_id) < 0) relByCard.get(l.to_id).push(l.from_id);
      });

      var cardDocs = cardsQ.map(function(row){
        return {id: row.id, data: cardRowToDoc(row, checkByCard.get(row.id) || [], histByCard.get(row.id) || [], relByCard.get(row.id) || [])};
      });

      var sessDocs = sessQ.map(function(row){
        return {id: row.card_id + "~" + row.id, data: sessRowToDoc(row)};
      });

      var pageDocs = [];
      cardsQ.forEach(function(row){
        if(Array.isArray(row.page)) pageDocs.push({id: row.id, data: {card: row.id, blocks: row.page}});
      });

      var folderList = foldersQ.map(function(row){
        return {id: row.id, name: row.name, parent: row.parent_id, example: !!row.example, home: !!row.home};
      });

      var customDocs = typesQ.map(function(r){
        var o = {key:r.key, label:r.label, plural:r.plural || undefined, short:r.short || undefined, color:r.color || undefined, statuses:r.statuses || [], fields:r.fields || [], home:!!r.home};
        if(r.sheet) o.sheet = r.sheet;
        return o;
      });
      // Postgres doesn't guarantee row order for a plain select, so the workspace-menu
      // drag-reorder (which only reorders a JS array client-side) would silently not
      // stick across a reload without this — keep the intended order in settings and
      // re-sort the rows by it, appending anything not yet listed (e.g. just created).
      var customOrder = Array.isArray(wsSettings.customOrder) ? wsSettings.customOrder : [];
      customDocs.sort(function(a, b){
        var ia = customOrder.indexOf(a.key), ib = customOrder.indexOf(b.key);
        if(ia < 0) ia = customOrder.length; if(ib < 0) ib = customOrder.length;
        return ia - ib;
      });

      var typeCfg = {
        custom: customDocs,
        extra: wsSettings.extra || {},
        style: wsSettings.style || {},
        builtinOrder: Array.isArray(wsSettings.builtinOrder) ? wsSettings.builtinOrder : undefined,
        off: Array.isArray(wsSettings.off) ? wsSettings.off : [],
        pack: wsSettings.pack || "",
        packs: Array.isArray(wsSettings.packs) ? wsSettings.packs : [],
        tour: !!wsSettings.tour
      };

      return {cardDocs:cardDocs, sessDocs:sessDocs, pageDocs:pageDocs, folderList:folderList, typeCfg:typeCfg};
    });
  }

  function makeCollSnap(docs){
    return {docs: docs.map(function(d){ return {id:d.id, data:function(){ return d.data; }}; }), metadata:{fromCache:false}};
  }
  function makeDocSnap(data){ return {exists:true, data:function(){ return data; }, metadata:{fromCache:false}}; }

  // A refresh() poll that was already in flight when a card got deleted can
  // still resolve with that card in it (its SELECT ran before the DELETE
  // committed) and land *after* the delete finished, silently resurrecting
  // it in the UI until the next poll corrects things — this is the "clear
  // doesn't actually clear, then it self-heals a moment later" bug. Filter
  // known-deleted ids out of every dispatched snapshot for a short window.
  var recentlyDeleted = new Map(); // id -> deleted-at ms
  var RECENTLY_DELETED_MS = 2 * REFRESH_MS;
  function markDeleted(id){ recentlyDeleted.set(id, Date.now()); }
  function pruneRecentlyDeleted(){
    var cutoff = Date.now() - RECENTLY_DELETED_MS;
    recentlyDeleted.forEach(function(at, id){ if(at < cutoff) recentlyDeleted.delete(id); });
  }

  function dispatch(snap){
    pruneRecentlyDeleted();
    if(recentlyDeleted.size){
      snap = Object.assign({}, snap, {cardDocs: snap.cardDocs.filter(function(d){ return !recentlyDeleted.has(d.id); })});
    }
    listeners.cards.forEach(function(l){ l.cb(makeCollSnap(snap.cardDocs)); });
    listeners.sess.forEach(function(l){ l.cb(makeCollSnap(snap.sessDocs)); });
    listeners.pages.forEach(function(l){ l.cb(makeCollSnap(snap.pageDocs)); });
    listeners.types.forEach(function(l){ l.cb(makeDocSnap(snap.typeCfg)); });
    listeners.folders.forEach(function(l){ l.cb(makeDocSnap({list:snap.folderList})); });
  }

  // The same race as recentlyDeleted, but for every write: a poll whose SELECTs ran
  // before a save committed can resolve after that save finished and the app has
  // cleared the card's dirty flag — the app then takes the stale row as the truth and
  // the edit visibly reverts until the next poll. So a snapshot that overlapped any
  // write (still in flight, or finished after the poll started) is thrown away and
  // re-fetched shortly after, once the writes have settled.
  var inFlight = 0, lastWriteEnd = 0, retryTimer = null;
  function track(fn, args){
    inFlight++;
    var p;
    try{ p = Promise.resolve(fn.apply(null, args)); }catch(e){ p = Promise.reject(e); }
    var done = function(){ inFlight--; lastWriteEnd = Date.now(); };
    return p.then(function(v){ done(); return v; }, function(e){ done(); throw e; });
  }
  function tracked(ref){
    ["set", "update", "delete"].forEach(function(k){
      var fn = ref[k];
      if(typeof fn === "function") ref[k] = function(){ return track(fn, arguments); };
    });
    return ref;
  }

  // ---------------- signed file URLs ----------------
  // The bucket is private (0004_private_storage.sql), so every file needs a signed
  // URL, but the app's fileSrc() must return a src string synchronously. So URLs are
  // signed ahead of time: every snapshot's file ids are signed (in batches) before
  // it's dispatched, and a fresh upload is signed before upload() resolves. A URL
  // asked for that isn't signed yet gets a unique placeholder, swapped in the DOM
  // for the real URL once it's ready.
  var SIGN_SECS = 24 * 3600, RESIGN_MS = 12 * 3600 * 1000;
  var PLACEHOLDER = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
  var signed = new Map(); // id -> {url, at}
  var signWanted = new Set(), signTimer = null;
  function fresh(id){ var s = signed.get(id); return s && Date.now() - s.at < RESIGN_MS; }
  function placeholderFor(id){ return PLACEHOLDER + "#tw=" + encodeURIComponent(id); }
  function swapPlaceholders(ids){
    ids.forEach(function(id){
      var s = signed.get(id); if(!s) return;
      var ph = placeholderFor(id);
      document.querySelectorAll('img[src="' + ph + '"]').forEach(function(el){ el.src = s.url; });
      document.querySelectorAll('a[href="' + ph + '"]').forEach(function(el){ el.href = s.url; });
    });
  }
  function signIds(ids){
    var need = ids.filter(function(id, i){ return id && ids.indexOf(id) === i && !fresh(id); });
    if(!need.length) return Promise.resolve();
    var batches = [];
    for(var i = 0; i < need.length; i += 100) batches.push(need.slice(i, i + 100));
    return Promise.all(batches.map(function(paths){
      return supabase.storage.from(BUCKET).createSignedUrls(paths, SIGN_SECS).then(function(r){
        if(r.error || !r.data) return;
        var at = Date.now();
        r.data.forEach(function(x){ if(x && x.signedUrl && x.path) signed.set(x.path, {url:x.signedUrl, at:at}); });
      }, function(){});
    })).then(function(){ swapPlaceholders(need); });
  }
  function signSoon(id){
    signWanted.add(id);
    if(signTimer) return;
    signTimer = setTimeout(function(){ var ids = Array.from(signWanted); signWanted.clear(); signTimer = null; signIds(ids); }, 30);
  }
  function snapFileIds(snap){
    var ids = [], add = function(f){ if(f && f.id) ids.push(f.id); };
    snap.cardDocs.forEach(function(d){ (d.data.files || []).forEach(add); });
    snap.pageDocs.forEach(function(d){ (d.data.blocks || []).forEach(function(b){ if(b && b.k === "i") add(b.f); }); });
    snap.sessDocs.forEach(function(d){ (d.data.files || []).forEach(add); });
    return ids;
  }

  function refresh(){
    if(refreshing) return refreshing;
    var started = Date.now();
    refreshing = loadAll().then(function(snap){
      return signIds(snapFileIds(snap)).then(function(){ return snap; });
    }).then(function(snap){
      refreshing = null;
      if(inFlight > 0 || lastWriteEnd >= started){
        if(!retryTimer) retryTimer = setTimeout(function(){ retryTimer = null; refresh(); }, 1500);
        return;
      }
      lastGood = snap; dispatch(snap);
    }, function(err){
      refreshing = null;
      Object.keys(listeners).forEach(function(k){ listeners[k].forEach(function(l){ if(l.errCb) l.errCb(err); }); });
    });
    return refreshing;
  }

  function startTimerOnce(){
    if(timerStarted) return; timerStarted = true;
    setInterval(refresh, REFRESH_MS);
    document.addEventListener("visibilitychange", function(){ if(document.visibilityState === "visible") refresh(); });
    window.addEventListener("focus", refresh);
  }

  function registerListener(kind, cb, errCb){
    listeners[kind].push({cb:cb, errCb:errCb});
    startTimerOnce();
    refresh();
  }

  // ---------------- write side ----------------
  function fetchCardRow(id){
    return supabase.from("cards").select("*").eq("id", id).maybeSingle().then(function(r){
      if(r.error) throw toDbError(r.error);
      return r.data;
    });
  }

  function replaceChecklist(cardId, items){
    return supabase.from("checklist_items").delete().eq("card_id", cardId).then(function(r){
      if(r.error) throw toDbError(r.error);
      if(!items.length) return;
      var rows = items.map(function(x, i){ return {id:x.id, card_id:cardId, text:x.t, done:!!x.done, pos:i}; });
      return supabase.from("checklist_items").insert(rows).then(function(r2){ if(r2.error) throw toDbError(r2.error); });
    });
  }

  function replaceHistory(cardId, hist){
    return supabase.from("history").delete().eq("card_id", cardId).then(function(r){
      if(r.error) throw toDbError(r.error);
      if(!hist.length) return;
      var rows = hist.map(function(h){ return {card_id:cardId, user_id:userId, text:h.w, created_at:h.at || new Date().toISOString()}; });
      return supabase.from("history").insert(rows).then(function(r2){ if(r2.error) throw toDbError(r2.error); });
    });
  }

  // Linking two cards writes a row with the *other* card as from_id too (the
  // reverse direction), and that other card's own flush() may not have
  // landed on the server yet (e.g. many cards created together, as in
  // loadSamples()) — its RLS check (via a lookup on `cards`) then denies the
  // insert since the row doesn't exist yet. That's a timing issue, not a
  // real permission problem, so retry briefly instead of surfacing it as
  // "view only" (see dbError() in app.js, which treats this error code as
  // permanent for the whole workspace).
  function upsertLinks(rows, attempt){
    attempt = attempt || 0;
    return supabase.from("card_links").upsert(rows, {onConflict:"from_id,to_id,kind"}).then(function(r2){
      if(!r2.error) return;
      var dbErr = toDbError(r2.error);
      if(dbErr.code === "invalid_argument" && attempt < 5){
        return new Promise(function(res){ setTimeout(res, 400 + attempt * 300); })
          .then(function(){ return upsertLinks(rows, attempt + 1); });
      }
      throw dbErr;
    });
  }

  function replaceLinks(cardId, rel){
    // Two plain .eq() deletes rather than building an .or() filter string by hand: cardId
    // is normally our own uid()-shaped id, but this keeps a stray "," or ")" in it from
    // being parsed as extra PostgREST filter syntax instead of a literal id value (RLS
    // still scopes every row to the caller's own workspace either way, so this is a
    // robustness fix, not a workspace-isolation one).
    return supabase.from("card_links").delete().eq("from_id", cardId).then(function(r){
      if(r.error) throw toDbError(r.error);
      return supabase.from("card_links").delete().eq("to_id", cardId);
    }).then(function(r){
      if(r && r.error) throw toDbError(r.error);
      if(!rel.length) return;
      var rows = [];
      rel.forEach(function(other){
        rows.push({from_id:cardId, to_id:other, kind:"related"});
        rows.push({from_id:other, to_id:cardId, kind:"related"});
      });
      return upsertLinks(rows);
    });
  }

  function applyCardWrite(id, obj, isFull){
    var has = function(k){ return isFull || Object.prototype.hasOwnProperty.call(obj, k); };
    return fetchCardRow(id).then(function(existing){
      if(!existing && !isFull) return Promise.reject({code:"invalid_argument"});

      var fields = Object.assign({}, existing ? (existing.fields || {}) : {});
      var columns = {};

      if(has("type")) columns.type_key = obj.type;
      if(has("title")) columns.title = obj.title;
      if(has("subtitle")) columns.subtitle = obj.subtitle;
      if(has("status")) columns.status = obj.status;
      if(has("tags")) columns.tags = obj.tags || [];
      if(has("folder")) columns.folder_id = obj.folder || null;
      if(has("home")) columns.home = !!obj.home;
      if(has("updated")) columns.updated_at = obj.updated ? new Date(obj.updated).toISOString() : new Date().toISOString();
      if(has("deleted")) columns.deleted_at = obj.deleted ? new Date(obj.deleted).toISOString() : null;

      if(has("f")){
        if(isFull) fields.f = obj.f || {};
        else{
          fields.f = Object.assign({}, fields.f || {});
          Object.keys(obj.f || {}).forEach(function(k){ if(obj.f[k] === "") delete fields.f[k]; else fields.f[k] = obj.f[k]; });
        }
      }
      if(has("src")) fields.src = obj.src || "";
      if(has("notes")) fields.notes = obj.notes || "";
      if(has("links")) fields._links = obj.links || [];
      if(has("files")) fields.files = obj.files || [];
      if(has("example")) fields.example = !!obj.example;
      if(has("archived")) fields.archived = obj.archived || "";
      if(has("template")) fields.template = !!obj.template;
      if(has("remind")) fields.remind = obj.remind || "";
      if(has("rpt")) fields.rpt = obj.rpt || null;
      columns.fields = fields;

      if(has("fav")){
        var favBy = (existing && existing.fav_by) || [];
        favBy = favBy.filter(function(u){ return u !== userId; });
        if(obj.fav && userId) favBy.push(userId);
        columns.fav_by = favBy;
      }

      var writeRow;
      if(!existing){
        var insertRow = Object.assign({id:id, workspace_id:workspaceId, type_key:obj.type || "issue", title:obj.title || ""}, columns);
        writeRow = supabase.from("cards").insert(insertRow).then(function(r){ if(r.error) throw toDbError(r.error); });
      } else {
        writeRow = supabase.from("cards").update(columns).eq("id", id).then(function(r){ if(r.error) throw toDbError(r.error); });
      }

      return writeRow.then(function(){
        var jobs = [];
        if(has("check")) jobs.push(replaceChecklist(id, obj.check || []));
        if(has("hist")) jobs.push(replaceHistory(id, obj.hist || []));
        if(has("rel")) jobs.push(replaceLinks(id, obj.rel || []));
        return Promise.all(jobs);
      }).then(function(){ return true; });
    });
  }

  function deleteCardFull(id){
    /* mark it gone before the request even goes out — a window-focus poll landing
       mid-flight (before the server confirms) would otherwise still see the row
       and hand the "deleted" card right back to the UI as live */
    markDeleted(id);
    return supabase.from("cards").delete().eq("id", id).then(function(r){
      if(r.error) throw toDbError(r.error);
      return true;
    });
  }

  function splitSessKey(key){ var i = key.indexOf("~"); return {cardId:key.slice(0, i), sessId:key.slice(i + 1)}; }
  /* a meeting page's free-placed blocks live in meeting_notes.blocks (0002); omit the key when
     there are none, so the doc matches what the app itself serialises for comparison */
  function sessRowToDoc(row){
    var d = {card: row.card_id, id: row.id, date: row.date, text: row.text || "", files: row.files || []};
    if(Array.isArray(row.blocks)) d.blocks = row.blocks;
    return d;
  }

  function sessDocRef(key){
    var parts = splitSessKey(key);
    return {
      set: function(obj){
        return supabase.from("meeting_notes").upsert({id:parts.sessId, card_id:parts.cardId, date:obj.date, text:obj.text || "", files:obj.files || [], blocks:Array.isArray(obj.blocks) ? obj.blocks : null})
          .then(function(r){ if(r.error) throw toDbError(r.error); return true; });
      },
      delete: function(){
        return supabase.from("meeting_notes").delete().eq("id", parts.sessId)
          .then(function(r){ if(r.error) throw toDbError(r.error); return true; });
      }
    };
  }

  // Its own column, written with a plain single-column UPDATE — no read-modify-write,
  // so it can never race the card body's own fields-column read-modify-write in
  // applyCardWrite() below (that race used to let one save silently clobber the
  // other, e.g. a just-added screenshot vanishing seconds after saving).
  function pageDocRef(id){
    return {
      set: function(obj){ return supabase.from("cards").update({page:obj.blocks || []}).eq("id", id).then(function(r){ if(r.error) throw toDbError(r.error); return true; }); },
      delete: function(){ return supabase.from("cards").update({page:null}).eq("id", id).then(function(r){ if(r.error) throw toDbError(r.error); return true; }); }
    };
  }

  function foldersDocRef(){
    return {
      set: function(obj){
        var list = (obj && obj.list) || [];
        return supabase.from("folders").select("id").eq("workspace_id", workspaceId).then(function(r){
          if(r.error) throw toDbError(r.error);
          var existingIds = r.data.map(function(x){ return x.id; });
          var newIds = list.map(function(f){ return f.id; });
          var gone = existingIds.filter(function(id){ return newIds.indexOf(id) < 0; });
          var p = gone.length ? supabase.from("folders").delete().in("id", gone).then(function(r2){ if(r2.error) throw toDbError(r2.error); }) : Promise.resolve();
          return p.then(function(){
            if(!list.length) return;
            var rows = list.map(function(f){ return {id:f.id, workspace_id:workspaceId, parent_id:f.parent || null, name:f.name, home:!!f.home, example:!!f.example}; });
            return supabase.from("folders").upsert(rows).then(function(r3){ if(r3.error) throw toDbError(r3.error); });
          });
        }).then(function(){ return true; });
      },
      onSnapshot: function(cb, errCb){ registerListener("folders", cb, errCb); }
    };
  }

  function typesDocRef(){
    return {
      set: function(cfg){
        var custom = (cfg && cfg.custom) || [];
        var newKeys = custom.map(function(t){ return t.key; });
        return supabase.from("card_types").select("key").eq("workspace_id", workspaceId).then(function(r){
          if(r.error) throw toDbError(r.error);
          var existingKeys = r.data.map(function(x){ return x.key; });
          var gone = existingKeys.filter(function(k){ return newKeys.indexOf(k) < 0; });
          var p = gone.length ? supabase.from("card_types").delete().eq("workspace_id", workspaceId).in("key", gone).then(function(r2){ if(r2.error) throw toDbError(r2.error); }) : Promise.resolve();
          return p.then(function(){
            if(!custom.length) return;
            var rows = custom.map(function(t){ return {workspace_id:workspaceId, key:t.key, label:t.label, plural:t.plural || null, short:t.short || null, color:t.color || null, statuses:t.statuses || [], fields:t.fields || [], home:!!t.home, sheet:t.sheet || null}; });
            return supabase.from("card_types").upsert(rows, {onConflict:"workspace_id,key"}).then(function(r3){ if(r3.error) throw toDbError(r3.error); });
          });
        }).then(function(){
          return supabase.from("workspaces").update({settings:{
            extra:(cfg && cfg.extra) || {}, style:(cfg && cfg.style) || {},
            builtinOrder:(cfg && cfg.builtinOrder) || [], customOrder:newKeys,
            off:(cfg && cfg.off) || [], pack:(cfg && cfg.pack) || "", packs:(cfg && cfg.packs) || [], tour:!!(cfg && cfg.tour)
          }}).eq("id", workspaceId);
        }).then(function(r){ if(r.error) throw toDbError(r.error); return true; });
      },
      onSnapshot: function(cb, errCb){ registerListener("types", cb, errCb); }
    };
  }

  function makeCollectionRef(name){
    return {onSnapshot: function(cb, errCb){ registerListener(name, cb, errCb); }};
  }

  function makeDb(){
    return {
      doc: function(path){
        var parts = path.split("/");
        if(parts[0] === "cards") return tracked({
          set: function(body){ return applyCardWrite(parts[1], body, true); },
          update: function(patch){ return applyCardWrite(parts[1], patch, false); },
          delete: function(){ return deleteCardFull(parts[1]); }
        });
        if(parts[0] === "sess") return tracked(sessDocRef(parts[1]));
        if(parts[0] === "pages") return tracked(pageDocRef(parts[1]));
        if(path === "meta/types") return tracked(typesDocRef());
        if(path === "meta/folders") return tracked(foldersDocRef());
        throw new Error("Unknown doc path: " + path);
      },
      collection: function(name){ return makeCollectionRef(name); }
    };
  }

  // ---------------- files (Supabase Storage) ----------------
  function makeAssets(){
    return {
      upload: function(file){
        if(file.size > 20 * 1024 * 1024) return Promise.reject({code:"too_large"});
        var path = workspaceId + "/" + randomId() + "-" + safeName(file.name);
        return supabase.storage.from(BUCKET).upload(path, file, {contentType:file.type, upsert:false}).then(function(res){
          if(res.error) return Promise.reject(toDbError(res.error));
          return signIds([path]).then(function(){ return {id:path, contentType:file.type}; });
        });
      },
      delete: function(id){
        return supabase.storage.from(BUCKET).remove([id]).then(function(res){
          if(res.error) return Promise.reject(toDbError(res.error));
          signed.delete(id);
          return true;
        });
      },
      // synchronous: a signed URL from the cache (re-signed in the background when
      // it's getting old), or a placeholder that's swapped out once it's signed
      url: function(id){
        var s = signed.get(id);
        if(s){ if(!fresh(id)) signSoon(id); return s.url; }
        signSoon(id);
        return placeholderFor(id);
      },
      // for the storage bar: this workspace's file bytes/count and the project's
      // database size (0004's workspace_usage()); if that function isn't there yet,
      // falls back to adding up the file listing, with no database figure
      usage: function(){
        var self = this;
        return supabase.rpc("workspace_usage", {ws_id:workspaceId}).then(function(r){
          if(!r.error && r.data) return {fileBytes:+r.data.file_bytes || 0, fileCount:+r.data.file_count || 0, dbBytes:+r.data.db_bytes || 0, fileLimit:1024 * 1024 * 1024, dbLimit:500 * 1024 * 1024};
          return self.list().then(function(files){
            return {fileBytes:files.reduce(function(n, f){ return n + (f.size || 0); }, 0), fileCount:files.length, dbBytes:null, fileLimit:1024 * 1024 * 1024, dbLimit:500 * 1024 * 1024};
          });
        });
      },
      // Every uploaded file this workspace owns — used by the "Clean up unused files"
      // workspace-menu action to find files no card/session references any more (removing
      // a block only ever deleted its own reference; anything already orphaned before that
      // fix, or missed some other way, needs to be found by actually listing storage).
      list: function(){
        function page(offset, acc){
          return supabase.storage.from(BUCKET).list(workspaceId, {limit:1000, offset:offset}).then(function(res){
            if(res.error) throw toDbError(res.error);
            var data = res.data || [];
            acc = acc.concat(data.map(function(f){ return {id:workspaceId + "/" + f.name, size:(f.metadata && f.metadata.size) || 0}; }));
            return data.length === 1000 ? page(offset + 1000, acc) : acc;
          });
        }
        return page(0, []);
      }
    };
  }

  function makeDownloads(){
    return {
      save: function(opts){
        try{
          var a = document.createElement("a");
          a.href = URL.createObjectURL(new Blob([opts.data], {type:"application/json"}));
          a.download = opts.filename;
          document.body.appendChild(a); a.click(); a.remove();
          return Promise.resolve();
        }catch(e){ return Promise.reject(e); }
      }
    };
  }

  return {
    use: function(cap){
      if(cap === "db") return Promise.resolve(makeDb());
      if(cap === "assets") return Promise.resolve(makeAssets());
      if(cap === "downloads") return Promise.resolve(makeDownloads());
      if(cap === "sample") return Promise.resolve(null); // Ask (AI) isn't wired up server-side yet
      return Promise.reject(new Error("capability not available: " + cap));
    }
  };
}
